/**
 * Maps validation failure codes from the order-status API to orderform field post names.
 *
 * The backend returns validation failure codes in the format:
 * "validation_type:field1, field2, ..." (e.g., "billing_address_missing_field:first_name, last_name")
 *
 * This utility extracts the field names and maps them to the corresponding
 * form field post names used by the orderform validation system.
 */

/**
 * Mapping from domain field names to form post names.
 * Domain fields come from backend validation codes, post names are used by form validation.
 *
 * Postname conventions:
 * - Shipping address (main contact form / fields_shipping_address): buyer_address_*
 * - Billing address (separate billing form / fields_billing_address): buyer_address_*_billing
 */
const BILLING_ADDRESS_FIELD_MAP: Record<string, string> = {
  first_name: 'buyer_address_first_name_billing',
  last_name: 'buyer_address_last_name_billing',
  email: 'buyer_address_email_billing',
  phone: 'buyer_address_phone_no_billing',
  phone_no: 'buyer_address_phone_no_billing',
  street: 'buyer_address_street_billing',
  street_number: 'buyer_address_street_number_billing',
  house_number: 'buyer_address_street_number_billing',
  city: 'buyer_address_city_billing',
  zip: 'buyer_address_zipcode_billing',
  zipcode: 'buyer_address_zipcode_billing',
  country: 'buyer_address_country_billing',
  state: 'buyer_address_state_billing',
  company: 'buyer_address_company_billing',
  salutation: 'buyer_address_salutation_billing',
};

const SHIPPING_ADDRESS_FIELD_MAP: Record<string, string> = {
  first_name: 'buyer_address_first_name',
  last_name: 'buyer_address_last_name',
  email: 'buyer_address_email',
  phone: 'buyer_address_phone_no',
  phone_no: 'buyer_address_phone_no',
  street: 'buyer_address_street',
  street_number: 'buyer_address_street_number',
  house_number: 'buyer_address_street_number',
  city: 'buyer_address_city',
  zip: 'buyer_address_zipcode',
  zipcode: 'buyer_address_zipcode',
  country: 'buyer_address_country',
  state: 'buyer_address_state',
  company: 'buyer_address_company',
  salutation: 'buyer_address_salutation',
};

const VAT_FIELD_MAP: Record<string, string> = {
  vat_id: 'vat_id',
  vat_number: 'vat_id',
};

const ORDER_DATA_FIELD_MAP: Record<string, string> = {
  email: 'buyer_address_email',
  language: 'language',
};

/**
 * Parses a validation code and extracts field names.
 *
 * @param code - The validation code (e.g., "billing_address_missing_field:first_name, last_name")
 * @returns Object with validation type and array of field names
 */
export function parseValidationCode(code: string): { type: string; fields: string[] } {
  if (!code) {
    return { type: '', fields: [] };
  }

  const colonIndex = code.indexOf(':');
  if (colonIndex === -1) {
    return { type: code, fields: [] };
  }

  const type = code.substring(0, colonIndex);
  const fieldsPart = code.substring(colonIndex + 1);
  const fields = fieldsPart
    .split(',')
    .map((f) => f.trim())
    .filter((f) => f.length > 0);

  return { type, fields };
}

/**
 * Maps a validation failure code to form field post names.
 *
 * @param validationCategory - The category from the API response (e.g., "billing_address", "vat")
 * @param code - The validation code containing optional field names
 * @returns Array of form field post names that should be highlighted
 */
export function mapValidationCodeToFieldNames(validationCategory: string, code: string | null): string[] {
  if (!code) {
    return [];
  }

  const { type, fields } = parseValidationCode(code);

  // Select the appropriate field map based on validation category
  let fieldMap: Record<string, string>;
  switch (validationCategory) {
    case 'billing_address':
      fieldMap = BILLING_ADDRESS_FIELD_MAP;
      break;
    case 'shipping_address':
    case 'shipping_destination':
      fieldMap = SHIPPING_ADDRESS_FIELD_MAP;
      break;
    case 'vat':
      fieldMap = VAT_FIELD_MAP;
      break;
    case 'order_data':
      fieldMap = ORDER_DATA_FIELD_MAP;
      break;
    default:
      // For categories without specific field mapping, return empty
      return [];
  }

  // If no specific fields mentioned, return all fields for that category
  if (fields.length === 0) {
    return Object.values(fieldMap);
  }

  // Map the mentioned fields to post names
  return fields.map((field) => fieldMap[field]).filter((postName) => postName !== undefined);
}

/**
 * Tries to parse a failure reason as JSON containing per-field error messages.
 * Returns null if parsing fails or the result is not a valid field->message object.
 */
function tryParseFieldMessages(reason: string | null): Record<string, string> | null {
  if (!reason) return null;

  try {
    const parsed = JSON.parse(reason);
    // Validate it's an object with string values
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      const isValidFieldMap = Object.values(parsed).every((v) => typeof v === 'string');
      if (isValidFieldMap) {
        return parsed as Record<string, string>;
      }
    }
  } catch {
    // Not JSON, return null
  }
  return null;
}

/**
 * Converts the failure_codes object from the API response to errors_by_postname format.
 *
 * For missing_field errors, the backend returns JSON-encoded per-field error messages
 * in failureReasons (e.g., {"street": "Please enter your street.", "city": "Please enter your city."}).
 * Each field gets its own specific error message.
 *
 * For other validation errors, the failureReasons contains a plain string message
 * that is applied to all affected fields.
 *
 * @param failureCodes - The failure_codes object from OrderStatusResponse
 * @param failureReasons - The failure_reasons object with error messages from backend
 * @returns Object mapping field post names to error messages
 */
export function mapFailureCodesToErrorsByPostname(
  failureCodes: Record<string, string | null>,
  failureReasons: Record<string, string | null>
): Record<string, string> {
  const errorsByPostname: Record<string, string> = {};

  for (const [category, code] of Object.entries(failureCodes)) {
    if (!code) continue;

    const { type, fields } = parseValidationCode(code);
    const isMissingFieldError = type.includes('missing_field');
    const reason = failureReasons[category];

    // Try to parse per-field messages for missing_field errors
    const fieldMessages = isMissingFieldError ? tryParseFieldMessages(reason) : null;
    const fallbackMessage = reason || 'Validation failed';

    // Select the appropriate field map
    let fieldMap: Record<string, string>;
    switch (category) {
      case 'billing_address':
        fieldMap = BILLING_ADDRESS_FIELD_MAP;
        break;
      case 'shipping_address':
      case 'shipping_destination':
        fieldMap = SHIPPING_ADDRESS_FIELD_MAP;
        break;
      case 'vat':
        fieldMap = VAT_FIELD_MAP;
        break;
      case 'order_data':
        fieldMap = ORDER_DATA_FIELD_MAP;
        break;
      default:
        continue;
    }

    // Map fields to postnames with appropriate error messages
    for (const field of fields) {
      const postName = fieldMap[field];
      if (postName) {
        // Use per-field message if available, otherwise fallback
        const message = fieldMessages?.[field] || fallbackMessage;
        errorsByPostname[postName] = message;
      }
    }
  }

  return errorsByPostname;
}

/**
 * Checks if any validation has failed in the order status response.
 *
 * @param validations - The validations object from OrderStatusResponse
 * @returns true if any validation status is 'failed'
 */
export function hasValidationFailure(validations: Record<string, string>): boolean {
  return Object.values(validations).some((status) => status === 'failed');
}

/**
 * Gets the primary error message from failure reasons.
 * Returns the first non-null failure reason found.
 *
 * @param failureReasons - The failure_reasons object from OrderStatusResponse
 * @returns The first error message found, or a generic message if none
 */
export function getPrimaryErrorMessage(failureReasons: Record<string, string | null>): string {
  for (const reason of Object.values(failureReasons)) {
    if (reason) {
      return reason;
    }
  }
  return 'Order validation failed. Please check your input and try again.';
}
